/* One episode. The canonical shareable unit of this station.
 *
 * Everything else on the site is a way of arriving here: the schedule points at
 * it, the archive grid points at it, a host's page is a list of them, and the
 * ticker names one. So this page carries the whole record — the sleeve, the
 * metadata grid, the scrubber, the notes, the tracklist and the way back out to
 * related sets — and it is the URL people paste.
 *
 * PLAY LOADS THE PERSISTENT PLAYER. There is no <audio> on this page and no
 * embed pretending to be one: the element lives above the router in App.tsx, so
 * pressing play here and then clicking through to the host's page does not stop
 * the music. That is the entire reason the player is mounted where it is.
 *
 * AUDIO STATES, ALL ORDINARY (D2, and see docs/DATA_MODEL.md)
 * -----------------------------------------------------------
 * The state is chosen by `clearance` first and by the presence of a URL
 * second, because `clearance` is what says WHICH LICENCE the audio sits under
 * and therefore what the listener is allowed to do with it.
 *
 *   a. SOUNDCLOUD — `soundcloud_url` is set. The public archive plays through
 *      SoundCloud's official widget inside the persistent station player.
 *   b. PERMANENT, self-hosted — `clearance` is own_repertoire or
 *      direct_licence, and `audio_url` is set. Tier A: the recording side is
 *      genuinely ours to clear, so this file is not on loan from anybody's
 *      replay allowance. Full player, full scrubber, no expiry.
 *   c. LEGACY LOCAL STATE — old `sena_replay` rows may retain an `audio_url` in
 *      the staff database, but the public API never serialises it while that
 *      clearance is not broadcastable. This page also refuses it defensively
 *      if a malformed response ever contains one.
 *   d. MIXCLOUD — legacy fallback when `mixcloud_url` is set. It links out.
 *   e. NEITHER. Also valid, also renderable: the tracklist, the notes and the
 *      credits are the record even when there is nothing to press.
 *
 * None of these is an error. None of them gets a red box and none of them gets
 * "sorry" — the page states the available archive source plainly.
 *
 * THE TRACKLIST IS GATED ON BROADCAST TIME. The station keeps programme detail
 * back as an editorial embargo, so nothing about what is in a set renders
 * before `broadcast_at`. After it, everything does.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, isAbortError } from '../api';
import {
  Artwork,
  Chip,
  ChipList,
  EpisodeCardLink,
  ErrorNote,
  MIXCLOUD_EMBED_CONSENT,
  MixcloudCard,
  Section,
  Spinner,
  Waveform,
  hostNames,
  useNarrow,
} from '../components';
import {
  formatClock,
  formatDuration,
  formatFullDate,
  formatTime,
  readTzMode,
  tzLabel,
} from '../format';
import { usePlayer } from '../player';
import { isPermanentClearance } from '../types';
import type { EpisodeCard, EpisodeDetail, Track } from '../types';

/** Rows of tracklist drawn before it folds. A long set runs to forty. */
const TRACKS_SHOWN = 15;

/* ------------------------------------------------------------------- peaks */

/**
 * audiowaveform v2 JSON — `data` interleaved [min, max, min, max, …] at 8 or
 * 16 bits, which is what api/hgr/audio.py writes. Reduced to one 0..1 amplitude
 * per bucket; the Waveform component does its own bucketing from there.
 */
function decodePeaks(payload: unknown): number[] | null {
  const source = payload as { data?: unknown; bits?: unknown } | null;
  const raw = Array.isArray(payload) ? payload : source?.data;
  if (!Array.isArray(raw) || raw.length < 2) return null;

  const bits = typeof source?.bits === 'number' ? source.bits : 8;
  const ceiling = bits === 16 ? 32_768 : 128;

  const values: number[] = [];
  for (let index = 0; index + 1 < raw.length; index += 2) {
    const low = Number(raw[index]);
    const high = Number(raw[index + 1]);
    if (!Number.isFinite(low) || !Number.isFinite(high)) continue;
    values.push(Math.min(1, Math.max(Math.abs(low), Math.abs(high)) / ceiling));
  }
  return values.length > 0 ? values : null;
}

/**
 * The peak file, or nothing.
 *
 * It lives in object storage rather than behind the API — it is a picture of
 * the audio, not a record about it — so this is the one place on the page that
 * goes anywhere except api.ts. If it will not load we say nothing and the
 * Waveform draws its deterministic envelope from the catalogue number instead;
 * a missing picture is not worth an error box on an episode that plays fine.
 */
function useEpisodePeaks(url: string | null): number[] | null {
  const [peaks, setPeaks] = useState<number[] | null>(null);

  useEffect(() => {
    setPeaks(null);
    if (!url) return;

    const controller = new AbortController();
    fetch(url, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('no peaks'))))
      .then((body: unknown) => {
        const values = decodePeaks(body);
        if (values) setPeaks(values);
      })
      .catch(() => {
        /* deliberately quiet — see above */
      });

    return () => controller.abort();
  }, [url]);

  return peaks;
}

/* --------------------------------------------------------------- the clock */

/**
 * Whether `iso` is in the past, kept true as the moment arrives rather than
 * only as the page loads. A tab left open across a show's start time has to
 * pick the tracklist up on its own; nobody reloads to check.
 *
 * FAILS CLOSED. An absent or unparseable timestamp reads as "not yet", because
 * the one caller is the tracklist embargo and the safe answer to "has this been
 * broadcast?" is no. `broadcast_at` is non-null on the wire,
 * so this only fires while the episode is still loading or if the server has
 * sent something malformed — in both cases hiding is right.
 */
function useHasPassed(iso: string | null | undefined): boolean {
  const target = iso ? Date.parse(iso) : Number.NaN;

  const [passed, setPassed] = useState(
    () => Number.isFinite(target) && Date.now() >= target,
  );

  useEffect(() => {
    if (!Number.isFinite(target)) {
      setPassed(false);
      return;
    }

    const wait = target - Date.now();
    if (wait <= 0) {
      setPassed(true);
      return;
    }

    setPassed(false);
    // setTimeout stores its delay in a signed 32-bit int: anything past ~24.9
    // days overflows and fires IMMEDIATELY, which here would publish a
    // tracklist a month early. A show that far out needs no timer at all.
    if (wait > 2_147_483_647) return;

    // A beat of slack so the flip lands after the moment rather than on it.
    const timer = window.setTimeout(() => setPassed(true), wait + 250);
    return () => window.clearTimeout(timer);
  }, [target]);

  return passed;
}

/* ------------------------------------------------------------------ theme */

/**
 * Whether the page is currently drawn dark. Only the Mixcloud widget needs to
 * know: it is somebody else's iframe and takes its ground colour as a
 * parameter, so it is the one element here that cannot inherit our tokens.
 *
 * `enabled` is MIXCLOUD_EMBED_CONSENT. With no consent on file there is no
 * iframe on this page (Terms of Use §4), and without this guard the hook would
 * still attach a media query listener and a MutationObserver on every episode
 * page to answer a question nothing asks. In the default build the flag is
 * false, so neither is ever attached.
 */
function useDarkGround(enabled: boolean): boolean {
  const read = () => {
    if (typeof window === 'undefined') return false;
    const chosen = document.documentElement.getAttribute('data-theme');
    if (chosen === 'dark') return true;
    if (chosen === 'light') return false;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  };

  const [dark, setDark] = useState(() => (enabled ? read() : false));

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setDark(read());
    query.addEventListener('change', onChange);

    // The site's own override is an attribute, not a media query.
    const observer = new MutationObserver(onChange);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    return () => {
      query.removeEventListener('change', onChange);
      observer.disconnect();
    };
  }, [enabled]);

  return dark;
}

/* ------------------------------------------------------------------ styles */

const CRUMB: CSSProperties = {
  padding: '14px clamp(12px, 4vw, 28px)',
  color: 'var(--muted)',
  borderBottom: 'var(--bw-hair) solid var(--edge)',
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
};

const HERO: CSSProperties = {
  display: 'flex',
  gap: 'clamp(20px, 4vw, 44px)',
  padding: '32px clamp(12px, 4vw, 28px) 40px',
  borderBottom: 'var(--bw) solid var(--edge)',
  flexWrap: 'wrap',
};

const HERO_HEAD: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  flexGrow: 1,
  minWidth: 0,
  flexBasis: '320px',
};

const TITLE: CSSProperties = {
  fontSize: 'clamp(2.75rem, 8vw, 6.5rem)',
};

const ACTIONS: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flexWrap: 'wrap',
  marginTop: '4px',
};

const META_CELL: CSSProperties = {
  borderRight: 'var(--bw-hair) solid var(--edge)',
  padding: '14px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  minWidth: 0,
};

const META_VALUE: CSSProperties = {
  fontSize: '1.1875rem',
  lineHeight: 1.1,
  fontVariationSettings: "'wdth' 96, 'wght' 800",
  textTransform: 'uppercase',
  overflowWrap: 'anywhere',
};

const WAVEBLOCK: CSSProperties = {
  padding: '24px clamp(12px, 4vw, 28px) 22px',
  borderBottom: 'var(--bw) solid var(--edge)',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
};

const NOTICE: CSSProperties = {
  padding: '24px clamp(12px, 4vw, 28px) 28px',
  borderBottom: 'var(--bw) solid var(--edge)',
  background: 'var(--surface)',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
};

const STANDFIRST: CSSProperties = {
  fontSize: 'clamp(1.25rem, 2.4vw, 1.4375rem)',
  lineHeight: 1.3,
  letterSpacing: '-.005em',
  fontVariationSettings: "'wght' 700",
  maxWidth: '56ch',
  margin: 0,
};

const TRACK_ROW: CSSProperties = {
  display: 'flex',
  gap: '16px',
  padding: '11px 22px',
  borderBottom: 'var(--bw-hair) solid var(--edge)',
  alignItems: 'baseline',
};

const TRACK_NO: CSSProperties = {
  fontFamily: 'var(--ff-mono)',
  fontWeight: 500,
  fontSize: 'var(--fs-meta)',
  letterSpacing: '.06em',
  color: 'var(--muted)',
  width: '26px',
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
};

/* -------------------------------------------------------------- fragments */

function paragraphs(body: string | null): string[] {
  if (!body) return [];
  return body
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
}

function TrackRow({ track }: { track: Track }) {
  return (
    <li style={TRACK_ROW}>
      <span style={TRACK_NO} aria-hidden="true">
        {String(track.position).padStart(2, '0')}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ fontSize: '0.9375rem', fontVariationSettings: "'wght' 700" }}>
          {track.artist}
        </span>{' '}
        <span style={{ fontSize: '0.9375rem' }}>— {track.title}</span>
      </span>
      {track.label ? (
        <span
          className="mono dim"
          style={{ marginLeft: 'auto', flexShrink: 0, fontSize: '0.625rem' }}
        >
          {track.label}
        </span>
      ) : null}
    </li>
  );
}

/* ------------------------------------------------------------------- page */

export default function Episode() {
  const { catalogueNo } = useParams<{ catalogueNo: string }>();
  const [tz] = useState(readTzMode);
  const narrow = useNarrow(900);
  const dark = useDarkGround(MIXCLOUD_EMBED_CONSENT);

  const [episode, setEpisode] = useState<EpisodeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  const [tracksOpen, setTracksOpen] = useState(false);
  const [shareNote, setShareNote] = useState<string | null>(null);
  const [playNote, setPlayNote] = useState<string | null>(null);

  useEffect(() => {
    if (!catalogueNo) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setEpisode(null);
    setTracksOpen(false);
    setShareNote(null);
    setPlayNote(null);

    api
      .getEpisode(catalogueNo, controller.signal)
      .then((found) => {
        setEpisode(found);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (isAbortError(failure)) return;
        setError(failure);
        setLoading(false);
      });

    return () => controller.abort();
  }, [catalogueNo, attempt]);

  useEffect(() => {
    document.title = episode
      ? `${episode.catalogue_no} ${episode.title} — Higher Ground Radio`
      : `${(catalogueNo ?? '').toUpperCase()} — Higher Ground Radio`;
  }, [episode, catalogueNo]);

  /* -------------------------------------------------------------- player */

  const player = usePlayer();
  const playerRef = useRef(player);
  playerRef.current = player;

  const relatedRef = useRef<AbortController | null>(null);
  useEffect(() => () => relatedRef.current?.abort(), []);

  const isCurrent =
    episode !== null &&
    player.mode === 'episode' &&
    player.current?.catalogue_no === episode.catalogue_no;

  const playThis = useCallback(() => {
    if (!episode) return;
    if (
      !episode.soundcloud_url &&
      (!episode.audio_url || !isPermanentClearance(episode.clearance))
    ) {
      return;
    }
    if (isCurrent) {
      playerRef.current.toggle();
      return;
    }
    playerRef.current.playEpisode(episode);
  }, [episode, isCurrent]);

  /* A related card carries only the card fields; the player needs the detail,
     which is where `audio_url` lives. One fetch, then the same persistent
     player everything else on the site uses. */
  const playRelated = useCallback((card: EpisodeCard) => {
    const current = playerRef.current;
    if (current.mode === 'episode' && current.current?.catalogue_no === card.catalogue_no) {
      current.toggle();
      return;
    }

    relatedRef.current?.abort();
    const controller = new AbortController();
    relatedRef.current = controller;
    setPlayNote(null);

    api
      .getEpisode(card.catalogue_no, controller.signal)
      .then((detail) => {
        /* No self-hosted file. WHY there is none differs by clearance — never
           held one (Tier B), or the replay window closed (Tier C) — and a card
           does not carry enough to tell those apart honestly. So the note says
           what the listener can do, not what we guess happened. */
        const hasClearedLocalAudio =
          detail.audio_url !== null && isPermanentClearance(detail.clearance);
        if (!detail.soundcloud_url && !hasClearedLocalAudio) {
          setPlayNote(
            detail.mixcloud_url
              ? `${detail.catalogue_no} has a legacy Mixcloud link. Open the episode to use it.`
              : `There is no playable copy of ${detail.catalogue_no} on file. Open it for the tracklist and the notes.`,
          );
          return;
        }
        playerRef.current.playEpisode(detail);
      })
      .catch((failure: unknown) => {
        if (isAbortError(failure)) return;
        setPlayNote(`We could not load ${card.catalogue_no}. Try again in a moment.`);
      });
  }, []);

  const peaks = useEpisodePeaks(
    episode?.audio_url && isPermanentClearance(episode.clearance) ? episode.waveform_url : null,
  );

  /* --------------------------------------------------------------- share */

  const share = useCallback(async () => {
    if (!episode) return;
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${episode.catalogue_no} ${episode.title}`, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareNote('Link copied.');
    } catch {
      // A cancelled share sheet lands here too, which is why this is not an
      // error box: the address bar is still the answer.
      setShareNote('Copy the address from the bar above to share this set.');
    }
  }, [episode]);

  useEffect(() => {
    if (!shareNote) return;
    const timer = window.setTimeout(() => setShareNote(null), 6_000);
    return () => window.clearTimeout(timer);
  }, [shareNote]);

  /* ---------------------------------------------------------------- view */

  const tracks = episode?.tracks ?? [];
  const shownTracks = tracksOpen ? tracks : tracks.slice(0, TRACKS_SHOWN);
  const notes = useMemo(() => paragraphs(episode?.body ?? null), [episode?.body]);

  /* SENA ART. 6(d): no advance publication of track data. Nothing about what is
     in a set — not the tracklist, not its length — renders until the broadcast
     has started. The server may well send the tracks with the episode (they are
     needed the instant the show goes on air, and an editor has to be able to
     load them beforehand), so the gate has to be here as well as there. */
  const broadcastPassed = useHasPassed(episode?.broadcast_at);

  if (loading) {
    return (
      <>
        <Section level={1} title={(catalogueNo ?? '').toUpperCase()} meta="Loading" />
        <div style={{ padding: '34px clamp(12px, 4vw, 28px)' }}>
          <Spinner label="Loading this episode" />
        </div>
      </>
    );
  }

  if (error || !episode) {
    return (
      <>
        <Section level={1} title="Episode" meta={(catalogueNo ?? '').toUpperCase()} />
        <ErrorNote error={error} what="this episode" onRetry={() => setAttempt((n) => n + 1)} />
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', padding: '0 clamp(12px, 4vw, 28px) 40px' }}>
          <Link className="btn" to="/archive">
            Back to the archive
          </Link>
          <Link className="btn-ghost" to="/residents">
            Browse the Rhythm Police
          </Link>
        </div>
      </>
    );
  }

  const who = hostNames(episode.hosts);
  const position = isCurrent ? player.position : 0;
  const duration =
    isCurrent && player.duration > 0 ? player.duration : episode.duration_seconds;
  const remaining = Math.max(0, duration - position);

  /* Only the two current-rights clearances may use a local delivery. This
     mirrors the API's fail-closed gate so a malformed response still cannot
     turn a preserved legacy URL into a play control. */
  const permanent = isPermanentClearance(episode.clearance);

  return (
    <>
      <nav className="mono" style={CRUMB} aria-label="Breadcrumb">
        <Link to="/archive">Archive</Link>
        <span aria-hidden="true">→</span>
        {episode.show ? (
          <>
            <Link to={`/archive?show=${encodeURIComponent(episode.show.slug)}`}>
              {episode.show.title}
            </Link>
            <span aria-hidden="true">→</span>
          </>
        ) : null}
        <span aria-current="page">{episode.catalogue_no}</span>
      </nav>

      <div style={HERO}>
        <div style={{ width: narrow ? '100%' : 'clamp(260px, 34vw, 460px)', flexShrink: 0 }}>
          <Artwork
            color={episode.artwork_color}
            imageUrl={episode.artwork_url}
            catalogueNo={episode.catalogue_no}
            label={episode.is_staff_pick ? 'Staff pick' : episode.catalogue_no}
            size="hero"
          />
        </div>

        <div style={HERO_HEAD}>
          {episode.is_staff_pick || episode.show ? (
            <ChipList label="This set">
              {episode.is_staff_pick ? <Chip color="pick">Staff pick</Chip> : null}
              {episode.show ? (
                <Chip to={`/archive?show=${encodeURIComponent(episode.show.slug)}`}>
                  {episode.show.title}
                </Chip>
              ) : null}
            </ChipList>
          ) : null}

          <h1 className="t-mega" style={TITLE}>
            {episode.title}
          </h1>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
            {episode.hosts.length > 0 ? (
              <div className="t-h3">
                w/{' '}
                {episode.hosts.map((host, index) => (
                  <span key={host.slug}>
                    {index > 0 ? ', ' : ''}
                    <Link to={`/residents/${encodeURIComponent(host.slug)}`}>{host.name}</Link>
                  </span>
                ))}
              </div>
            ) : (
              <div className="t-h3">Station broadcast</div>
            )}
            <div className="mono-13 dim">
              {formatFullDate(episode.broadcast_at, tz)} · {episode.location_long} ·{' '}
              {formatDuration(episode.duration_seconds)}
            </div>
          </div>

          {episode.genres.length > 0 ? (
            <ChipList label="Genres">
              {episode.genres.map((genre) => (
                <Chip key={genre.slug} color={genre.color_token} to={`/genre/${genre.slug}`}>
                  {genre.name}
                </Chip>
              ))}
            </ChipList>
          ) : null}

          <div style={ACTIONS}>
            {episode.soundcloud_url || (episode.audio_url && permanent) ? (
              <button
                type="button"
                className="btn-lg"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: narrow ? '10px' : '16px',
                  maxWidth: '100%',
                  minWidth: 0,
                }}
                onClick={playThis}
                aria-pressed={isCurrent && player.isPlaying}
              >
                <span
                  className={isCurrent && player.isPlaying ? 'pausebars' : 'tri'}
                  aria-hidden="true"
                />
                {isCurrent && player.isPlaying
                  ? 'Pause set'
                  : isCurrent && player.isLoading
                    ? 'Loading SoundCloud…'
                    : isCurrent && player.soundCloudReady
                      ? 'Play set'
                      : isCurrent
                        ? 'Resume set'
                        : episode.soundcloud_url
                          ? narrow
                            ? 'Load player'
                            : 'Load SoundCloud player'
                          : 'Play set'}
              </button>
            ) : null}

            <button type="button" className="btn-ghost" onClick={() => void share()}>
              Share
            </button>

            {episode.soundcloud_url ? (
              <a
                className="btn-ghost"
                href={episode.soundcloud_url}
                target="_blank"
                rel="noreferrer"
              >
                Open on SoundCloud →
              </a>
            ) : null}

            {episode.mixcloud_url ? (
              <a
                className="btn-ghost"
                href={episode.mixcloud_url}
                target="_blank"
                rel="noreferrer"
              >
                Open on Mixcloud →
              </a>
            ) : null}
          </div>

          <p className="mono dim" role="status" style={{ margin: 0, lineHeight: 1.6 }}>
            {shareNote ?? ' '}
          </p>

          {episode.hosts.length > 0 ? (
            <div className="mono dim" style={{ lineHeight: 1.8 }}>
              {episode.hosts.map((host, index) => (
                <span key={host.slug}>
                  {index > 0 ? ' · ' : ''}
                  <Link to={`/residents/${encodeURIComponent(host.slug)}`}>{host.name} profile</Link>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
          borderBottom: 'var(--bw) solid var(--edge)',
        }}
      >
        <div style={META_CELL}>
          <div className="mono dim">Catalogue</div>
          <div style={META_VALUE}>{episode.catalogue_no}</div>
        </div>
        <div style={META_CELL}>
          <div className="mono dim">Broadcast</div>
          <div style={META_VALUE}>
            {formatFullDate(episode.broadcast_at, tz)} · {formatTime(episode.broadcast_at, tz)}
          </div>
          <div className="mono dim">{tzLabel(tz, episode.broadcast_at)}</div>
        </div>
        <div style={META_CELL}>
          <div className="mono dim">Location</div>
          <div style={META_VALUE}>
            {episode.location_short} · {episode.location_long}
          </div>
        </div>
        {/* The runtime is the booked length of a programme and says nothing
            about its contents, so it is fine before broadcast. The track count
            belongs to the same editorial embargo as the tracklist, which also
            keeps this cell coherent with the tracklist column. */}
        <div style={{ ...META_CELL, borderRight: 0 }}>
          <div className="mono dim">Runtime</div>
          <div style={META_VALUE}>
            {formatClock(episode.duration_seconds)}
            {broadcastPassed && tracks.length > 0 ? ` · ${tracks.length} tracks` : ''}
          </div>
          {!broadcastPassed ? (
            <div className="mono dim">Tracklist after broadcast</div>
          ) : tracks.length === 0 ? (
            <div className="mono dim">No tracklist</div>
          ) : null}
        </div>
      </div>

      {/* ---- audio state a: SoundCloud, through the official lazy widget.
              Local audio remains the fallback in the branches below. */}
      {/* ---- audio state b: PERMANENT, self-hosted. The recording side is ours
              to clear (art. 6 WNR) or cleared in writing by a named grantor, so
              this is the ordinary full player with a scrubber. */}
      {episode.soundcloud_url ? (
        <div style={WAVEBLOCK}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
            <span className="mono-13">{formatClock(position)}</span>
            <span className="mono dim">elapsed</span>
            <span className="mono-13" style={{ marginLeft: 'auto' }}>
              −{formatClock(remaining)}
            </span>
            <span className="mono dim">remaining</span>
          </div>

          <Waveform
            peaks={peaks}
            seed={episode.catalogue_no}
            positionSeconds={position}
            durationSeconds={duration}
            onSeek={isCurrent ? player.seek : undefined}
            height={56}
            label={`Position in ${episode.title}`}
          />

          <div className="mono dim" style={{ lineHeight: 1.7 }}>
            {isCurrent && player.soundCloudReady && !player.isPlaying
              ? 'SoundCloud ready — press play again to start the set.'
              : isCurrent
                ? 'Drag the bars to seek, or use the persistent player below.'
                : 'Load the official SoundCloud player, then press play once it is ready.'}{' '}
            SoundCloud is the public archive source; no SoundCloud player or script loads until
            you press play.{' '}
            <a href={episode.soundcloud_url} target="_blank" rel="noreferrer">
              Higher Ground Radio on SoundCloud →
            </a>
          </div>
        </div>
      ) : episode.audio_url && permanent ? (
        <div style={WAVEBLOCK}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
            <span className="mono-13">{formatClock(position)}</span>
            <span className="mono dim">elapsed</span>
            <span className="mono-13" style={{ marginLeft: 'auto' }}>
              −{formatClock(remaining)}
            </span>
            <span className="mono dim">remaining</span>
          </div>

          <Waveform
            peaks={peaks}
            seed={episode.catalogue_no}
            positionSeconds={position}
            durationSeconds={duration}
            onSeek={isCurrent ? player.seek : undefined}
            height={56}
            label={`Position in ${episode.title}`}
          />

          <div className="mono dim" style={{ lineHeight: 1.7 }}>
            {isCurrent
              ? 'Drag anywhere on the bars to seek, or use the arrow keys on the slider.'
              : 'Press play and the bars become a scrubber.'}{' '}
            {peaks
              ? 'The shape is the measured peaks of this set.'
              : 'We have no peak file for this set, so the shape is drawn from its catalogue number.'}{' '}
            This one stays. The recording is cleared for a permanent copy here, so it is not
            borrowed from a replay window and there is no date on it.
          </div>
        </div>
      ) : episode.audio_url ? (
        /* Defense in depth for a malformed response. The API deliberately
           keeps legacy delivery data out of public serialization, but this
           branch also refuses to turn it into a transport control. */
        <div style={NOTICE}>
          <div className="mono dim">Legacy local copy unavailable</div>
          <p className="t-body">
            This entry still has a historical delivery record, but its current clearance does not
            allow us to serve that copy. The tracklist and notes stay here.
          </p>
        </div>
      ) : episode.mixcloud_url ? (
        /* ---- audio state d: legacy MIXCLOUD link. */
        <div style={NOTICE}>
          <div className="mono dim">Legacy Mixcloud copy</div>
          <p className="t-body">
            {episode.clearance === 'mixcloud_only'
              ? `This older archive entry has a Mixcloud link and no SoundCloud track attached yet.`
              : episode.audio_expires_at
                ? `We stopped hosting this set on ${formatFullDate(episode.audio_expires_at, tz)}. Its older external copy is on Mixcloud.`
                : 'We no longer host this set ourselves. Its older external copy is on Mixcloud.'}{' '}
            The tracklist and the notes stay here.
          </p>

          {/* LINK OUT, DO NOT EMBED — and that is the default, not a fallback.
              Mixcloud's Terms of Use §4 prohibit widget use for "any form of
              commercial use OR non personal / individual use (unless pursuant
              to prior written consent…)". The OR is what settles it: being
              non-profit answers the first prong and does nothing to the second,
              and a station site is not a personal website. The iframe does
              render if you ask for it, which makes this unenforced rather than
              permitted.

              MIXCLOUD_EMBED_CONSENT is false unless the build environment says
              otherwise (see components/MixcloudCard.tsx). The day written
              consent arrives, setting VITE_MIXCLOUD_EMBED_CONSENT=true is the
              entire change — this branch is already here and already correct. */}
          {MIXCLOUD_EMBED_CONSENT ? (
            <iframe
              title={`${episode.catalogue_no} ${episode.title} on Mixcloud`}
              src={`https://www.mixcloud.com/widget/iframe/?hide_cover=1&light=${dark ? 0 : 1}&feed=${encodeURIComponent(episode.mixcloud_url)}`}
              width="100%"
              height="120"
              style={{ border: 'var(--bw) solid var(--edge)', display: 'block', maxWidth: '760px' }}
              allow="autoplay"
              loading="lazy"
            />
          ) : (
            <MixcloudCard
              url={episode.mixcloud_url}
              title={episode.title}
              catalogueNo={episode.catalogue_no}
              artworkColor={episode.artwork_color}
              meta={
                who
                  ? `${who} · ${formatDuration(episode.duration_seconds)}`
                  : formatDuration(episode.duration_seconds)
              }
            />
          )}
        </div>
      ) : (
        /* ---- audio state e: nothing to press. Still a record. */
        <div style={NOTICE}>
          <div className="mono dim">No playable copy</div>
          <p className="t-body">
            {episode.audio_expires_at
              ? `Our own copy came down on ${formatFullDate(episode.audio_expires_at, tz)}, and there is no SoundCloud or legacy Mixcloud link on file yet.`
              : 'There is no playable copy of this set on file — no SoundCloud track, local replay or legacy Mixcloud link.'}{' '}
            That is usually a set still going through publishing, and it is sometimes one we were
            asked to take down. Either way the tracklist and the notes below are the record, and
            they stay.
          </p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Link className="btn-ghost" to="/archive">
              Back to the archive
            </Link>
            {episode.hosts[0] ? (
              <Link
                className="btn-ghost"
                to={`/residents/${encodeURIComponent(episode.hosts[0].slug)}`}
              >
                More from {episode.hosts[0].name}
              </Link>
            ) : null}
          </div>
        </div>
      )}

      {playNote ? (
        <p
          className="mono dim"
          role="status"
          style={{ margin: 0, padding: '12px clamp(12px, 4vw, 28px)', lineHeight: 1.6 }}
        >
          {playNote}
        </p>
      ) : null}

      <div
        style={{
          display: 'flex',
          flexDirection: narrow ? 'column' : 'row',
          borderBottom: 'var(--bw) solid var(--edge)',
        }}
      >
        <div
          style={{
            flexGrow: 1,
            minWidth: 0,
            padding: '28px clamp(12px, 4vw, 28px)',
            borderRight: narrow ? 0 : 'var(--bw) solid var(--edge)',
            borderBottom: narrow ? 'var(--bw) solid var(--edge)' : 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
          }}
        >
          <div className="mono dim">Show notes</div>

          {episode.standfirst ? <p style={STANDFIRST}>{episode.standfirst}</p> : null}

          {notes.map((block, index) => (
            <p className="t-body" key={index}>
              {block}
            </p>
          ))}

          {!episode.standfirst && notes.length === 0 ? (
            <p className="t-body">
              Nobody has written this one up yet. The tracklist, the date and the runtime are what we
              have — if you were in the room and want to fill in the rest, the address is in the
              footer.
            </p>
          ) : null}

          {episode.is_staff_pick && episode.staff_pick_note ? (
            <div
              style={{
                borderLeft: 'var(--bw-on) solid var(--edge)',
                paddingLeft: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div className="mono dim">Why this is a staff pick</div>
              <p className="t-body">{episode.staff_pick_note}</p>
            </div>
          ) : null}
        </div>

        <div style={{ width: narrow ? '100%' : '460px', flexShrink: 0, minWidth: 0 }}>
          <div
            style={{
              padding: '18px 22px',
              borderBottom: 'var(--bw) solid var(--edge)',
              display: 'flex',
              alignItems: 'baseline',
              gap: '14px',
              flexWrap: 'wrap',
            }}
          >
            <h2 className="t-h3">Tracklist</h2>
            <span className="mono dim">
              {!broadcastPassed
                ? 'After broadcast'
                : tracks.length > 0
                  ? `${tracks.length} tracks · untimed`
                  : 'Not published'}
            </span>
          </div>

          {/* Before `broadcast_at` this column says what it is waiting for and
              when — an honest empty state, not a blank. The station keeps the
              tracklist back as an editorial choice until the first broadcast;
              `useHasPassed` then reveals it without a reload. */}
          {!broadcastPassed ? (
            <div
              style={{
                padding: '22px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <p className="t-body">
                Nothing here until it has aired. We hold the tracklist until the first broadcast,
                then publish it when the programme starts —{' '}
                {formatFullDate(episode.broadcast_at, tz)} at{' '}
                {formatTime(episode.broadcast_at, tz)} {tzLabel(tz, episode.broadcast_at)}.
              </p>
              <Link
                className="mono"
                to="/schedule"
                style={{ display: 'inline-flex', alignItems: 'center', minHeight: '44px' }}
              >
                See it on the schedule →
              </Link>
            </div>
          ) : tracks.length === 0 ? (
            <div
              style={{
                padding: '22px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <p className="t-body">
                No tracklist for this set. Some are sent in weeks late and some never arrive — a
                two-hour set of unreleased dubs often has no ID to give. We add them whenever the
                host sends one.
              </p>
              <Link
                className="mono"
                to="/archive?tracklist=1"
                style={{ display: 'inline-flex', alignItems: 'center', minHeight: '44px' }}
              >
                Browse the sets that do have one →
              </Link>
            </div>
          ) : (
            <>
              <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {shownTracks.map((track) => (
                  <TrackRow key={`${track.position}-${track.artist}-${track.title}`} track={track} />
                ))}
              </ol>
              {tracks.length > TRACKS_SHOWN ? (
                <div style={{ padding: '14px 22px 18px' }}>
                  <button
                    type="button"
                    className="mono"
                    aria-expanded={tracksOpen}
                    style={{
                      minHeight: '44px',
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                    onClick={() => setTracksOpen((open) => !open)}
                  >
                    {tracksOpen
                      ? `Show the first ${TRACKS_SHOWN} tracks`
                      : `Show all ${tracks.length} tracks`}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {episode.related.length > 0 ? (
        <>
          <Section title="More like this" meta="Matched on host and genre" />
          <div className="cardgrid" style={{ paddingTop: '26px', paddingBottom: '40px' }}>
            {episode.related.map((card) => (
              <EpisodeCardLink
                key={card.catalogue_no}
                episode={card}
                tz={tz}
                onPlay={playRelated}
                isPlaying={
                  player.mode === 'episode' &&
                  player.current?.catalogue_no === card.catalogue_no &&
                  player.isPlaying
                }
                genreHref={(slug) => `/genre/${slug}`}
              />
            ))}
          </div>
        </>
      ) : (
        <>
          <Section title="More like this" meta="Nothing matched" />
          <div style={{ padding: '26px clamp(12px, 4vw, 28px) 40px' }}>
            <p className="t-body">
              We could not find another set that shares a host or a genre with this one. That
              happens early in a strand — {who || 'this host'} has not been back yet.
            </p>
            <p style={{ marginTop: '18px' }}>
              <Link className="btn" to="/archive">
                Everything in the archive
              </Link>
            </p>
          </div>
        </>
      )}
    </>
  );
}
